import { useNavigate } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Dialog, F0Select } from '@factorialco/f0-react'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import {
  PagesFolderInformationDocument,
  PagesFolderTypesDocument,
  PagesMoveHandbookPageDocument,
} from 'generated/resources/graphql'

import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface MovePageModalProps {
  pageId: number
  initialFolderType: string
  onClose: () => void
  redirectInPage?: boolean
}

interface MovePageForm {
  folderType: string
}

export const MovePageModal = ({
  pageId,
  initialFolderType,
  onClose,
  redirectInPage = false,
}: MovePageModalProps) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const { loading: isLoadingFolderTypes, data: folderTypes } = useQuery(PagesFolderTypesDocument)

  const [movePage, { loading: isLoadingMovePage }] = useMutation(PagesMoveHandbookPageDocument, {
    extractorFn: (data) => data.handbookPagesMutations.updateHandbookpagesPage,
    refetchQueries: [PagesFolderInformationDocument],
  })

  const form = useForm<MovePageForm>({
    defaultValues: {
      folderType: initialFolderType,
    },
    validate(values) {
      const errors: FormErrors<MovePageForm> = {}
      if (!values.folderType || values.folderType.trim() === '') {
        errors.folderType = i18n.t('common.validations.empty')
      }
      return errors
    },

    async onSubmit(values, { setErrors }) {
      const variables = {
        folderType: values.folderType,
        id: pageId,
      }

      const result = await movePage({
        variables,
      })

      const handler = handleResult(result, (errors) => setErrors(errors))

      if (handler?.success) {
        if (redirectInPage) {
          navigate(
            navigation.pages.folder.page.fullPath({
              folderSlug: values.folderType,
              pageId: String(pageId),
            })
          )
        } else {
          navigate(
            navigation.pages.folder.fullPath({
              folderSlug: values.folderType,
            })
          )
        }
        app.setFlash({
          type: 'correct',
          message: i18n.t('pages.page.move_page.success'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('common.error'),
        })
      }

      onClose()

      return handler
    },
  })

  return (
    <F0Dialog
      isOpen={!!pageId}
      onClose={() => {
        if (!isLoadingMovePage) {
          onClose()
        }
      }}
      title={isLoadingMovePage || isLoadingFolderTypes ? '' : i18n.t('pages.page.move_page.title')}
      primaryAction={
        isLoadingMovePage || isLoadingFolderTypes
          ? undefined
          : {
              label: i18n.t('common.save'),
              onClick: () => form.handleSubmit(),
              loading: isLoadingMovePage,
            }
      }
      secondaryAction={
        isLoadingMovePage || isLoadingFolderTypes
          ? undefined
          : {
              label: i18n.t('common.cancel'),
              onClick: () => onClose(),
            }
      }
    >
      {isLoadingMovePage || isLoadingFolderTypes ? (
        <Box
          paddingX="s16"
          paddingY="s120"
          gap="s12"
          alignItems="center"
          justifyContent="center"
          height="full"
        >
          <Spinner />
          <Text.Normal size="200">{i18n.t('common.loading')}</Text.Normal>
        </Box>
      ) : (
        <F0Select
          label={i18n.t('pages.page.move_page.folder_type.label')}
          placeholder={i18n.t('pages.page.move_page.folder_type.placeholder')}
          onChange={(value: string) => form.setFieldValue('folderType', value)}
          options={
            folderTypes?.handbookPages.foldersConnection.nodes.map((folder) => ({
              label: folder.name,
              value: folder.slug,
            })) ?? []
          }
          value={form.values.folderType}
          error={form.errors.folderType}
          size="md"
        />
      )}
    </F0Dialog>
  )
}
